import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useGraphql } from "~/api-client";
import { setBetaEnabled } from "~/app/actions/cookies";
import type { FeatureKey } from "../graphql-client";
import { hasClientCookie } from "../utils/cookies";

export function useFeatures(locationRef: string | undefined) {
  const { getFeatures } = useGraphql();

  const { data, isLoading } = useQuery({
    queryKey: ["features", locationRef],
    queryFn: () => getFeatures(locationRef),
    refetchOnWindowFocus: false,
    staleTime: 20000,
  });

  return useMemo(() => {
    const isFeatureEnabled = (key: FeatureKey) => {
      if (data?.features) {
        const feature = data.features.find((f) => f.key === key);
        return feature?.enabled || false;
      }

      return false;
    };

    return {
      data,
      isLoading,
      isFeatureEnabled,
    };
  }, [data, isLoading]);
}

export function useIsFeatureEnabled(
  key: FeatureKey,
  locationRef: string | undefined,
): boolean {
  const { data } = useFeatures(locationRef);

  if (data?.features) {
    const feature = data.features.find((f) => f.key === key);
    return feature?.enabled || false;
  }
  return false;
}

export function useIsBetaEnabled(): {
  enabled: boolean;
  setEnabled: () => void;
} {
  const [isEnabled, setIsEnabled] = useState(() =>
    hasClientCookie("beta_enabled"),
  );

  return {
    enabled: isEnabled,
    setEnabled: () => {
      if (isEnabled) return;
      setBetaEnabled()
        .then(() => setIsEnabled(true))
        .catch((error) => console.error("Failed to enable beta", error));
    },
  };
}
